import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Connects to data-controller="side-menu-edit"
//
// Edit mode for the nav, rendered by the server on ?side_menu=edit. Every row
// is an `li[data-key]`; a group adds `data-group` and owns a nested `ul`, and
// each row remembers where the catalog put it in `data-default-parent`.
//
// One Sortable per list, all in the same group, so a row can cross between the
// root list and a group's list. `put` is the client half of
// SideMenu::Layout#resolve_parent — the server re-checks every row, this only
// stops a drop the server would refuse. Each change PATCHes the whole tree and
// a rejected save puts the DOM back, so what is on screen is always the last
// state the server accepted.
//
// Each save is announced on `window` as a `side-menu-save` event
// ({ detail: { state } }: saving, saved, error) for a save-indicator to show.
// `savedOnly` targets (the template's Set to default) appear after a save.
export default class extends Controller<HTMLElement> {
  static targets = ["list", "hint", "savedOnly"]
  static values = {
    url: String,
    hintDefault: String,
    hintHiddenOne: String,
    hintHiddenOther: String
  }

  declare readonly listTargets: HTMLElement[]
  declare readonly hintTarget: HTMLElement
  declare readonly hasHintTarget: boolean
  declare readonly savedOnlyTargets: HTMLElement[]
  declare readonly urlValue: string
  declare readonly hintDefaultValue: string
  declare readonly hintHiddenOneValue: string
  declare readonly hintHiddenOtherValue: string

  private sortables: Sortable[] = []
  // Saves overlap when edits come quickly. The indicator settles only once
  // none is in flight, on the outcome of the newest.
  private inFlight = 0
  private generation = 0
  private newestFailed = false

  connect() {
    this.sortables = this.listTargets.map((list) => new Sortable(list, {
      group: {
        name: 'side-menu',
        pull: true,
        put: (to, _from, dragEl) => this.canDrop(to.el, dragEl)
      },
      draggable: 'li[data-key]',
      // A row drags from anywhere on it — grip, icon or label — except the
      // eye, whose click has to land.
      handle: '.side-menu-edit-row',
      filter: '.side-menu-eye',
      preventOnFilter: false,
      animation: 150,
      direction: 'vertical',
      // The aside clips horizontally when docked and is fixed + transformed on
      // mobile, either of which would cut a drag image drawn inside it.
      forceFallback: true,
      fallbackOnBody: true,
      fallbackClass: 'side-menu-edit-ghost',
      ghostClass: 'side-menu-edit-placeholder',
      // A touch has to rest on the grip first, or the menu could not be
      // scrolled with a finger that started on a row.
      delay: 150,
      delayOnTouchOnly: true,
      swapThreshold: 0.65,
      // A group emptied by dragging its last child out still has to accept one
      // back, and a collapsed-height list has no room to aim at.
      emptyInsertThreshold: 8,
      onEnd: (event) => this.onEnd(event)
    }))
  }

  disconnect() {
    this.sortables.forEach((sortable) => sortable.destroy())
    this.sortables = []
  }

  // A group's row is its <summary>, and a drag starts with a press on it: a
  // plain click must not fold away the list its children are dropped into.
  keepOpen(event: Event) {
    event.preventDefault()
  }

  // The eye on every hideable row. It lives inside a <summary> on a group, so
  // the default (toggling the disclosure) has to go.
  toggleHidden(event: Event) {
    event.preventDefault()

    const button = event.currentTarget as HTMLElement
    const row = button.closest<HTMLElement>('li[data-key]')
    if (!row) return

    const hidden = this.setHidden(row, button, row.dataset.hidden !== 'true')
    this.save().catch(() => this.setHidden(row, button, !hidden))
  }

  // Alt+Arrow on the grip, for anyone who cannot drag. It reorders within the
  // row's own list only: the moves a keyboard can express unambiguously.
  move(event: KeyboardEvent) {
    const up = event.key === 'ArrowUp'
    if (!event.altKey || (!up && event.key !== 'ArrowDown')) return

    const grip = event.currentTarget as HTMLElement
    const row = grip.closest<HTMLElement>('li[data-key]')
    const sibling = up ? row?.previousElementSibling : row?.nextElementSibling
    if (!row || !sibling?.matches('li[data-key]')) return

    event.preventDefault()
    sibling.insertAdjacentElement(up ? 'beforebegin' : 'afterend', row)
    grip.focus() // moving the row took focus out of the document with it
    this.save().catch(() => sibling.insertAdjacentElement(up ? 'afterend' : 'beforebegin', row))
  }

  // A group lives at the top level or where the catalog put it; a leaf lives at
  // the top level, in any top-level group, or where the catalog put it. The
  // root list takes anything.
  private canDrop(list: HTMLElement, dragEl: HTMLElement): boolean {
    const group = list.closest<HTMLElement>('li[data-key]')
    if (!group) return true
    if (dragEl.dataset.defaultParent === group.dataset.key) return true
    if ('group' in dragEl.dataset) return false

    return !group.parentElement?.closest('li[data-key]')
  }

  private onEnd(event: Sortable.SortableEvent) {
    if (event.to === event.from && event.newIndex === event.oldIndex) return

    this.save().catch(() => this.putBack(event))
  }

  // Detach first: within one list, oldIndex counts the positions the row is
  // still occupying one of.
  private putBack(event: Sortable.SortableEvent) {
    event.item.remove()
    event.from.insertBefore(event.item, event.from.children[event.oldIndex ?? 0] ?? null)
  }

  // The row's flag is what the stylesheet dims and what payload() sends; the
  // button is named for the state it switches to.
  private setHidden(row: HTMLElement, button: HTMLElement, hidden: boolean): boolean {
    row.dataset.hidden = String(hidden)
    button.setAttribute('aria-pressed', String(hidden))

    const label = hidden ? button.dataset.showLabel : button.dataset.hideLabel
    if (label) button.setAttribute('aria-label', label)
    this.syncHint()

    return hidden
  }

  // The whole tree in document order, so the server never has to reconcile a
  // partial move.
  private payload() {
    return Array.from(this.element.querySelectorAll<HTMLElement>('li[data-key]')).map((row) => ({
      key: row.dataset.key ?? '',
      parent: row.parentElement?.closest<HTMLElement>('li[data-key]')?.dataset.key ?? null,
      hidden: row.dataset.hidden === 'true'
    }))
  }

  private async save() {
    const generation = ++this.generation
    this.inFlight++
    this.announce('saving')

    try {
      await this.request()
      if (generation === this.generation) this.newestFailed = false
      this.savedOnlyTargets.forEach((element) => { element.hidden = false })
    } catch (error) {
      if (generation === this.generation) this.newestFailed = true
      throw error
    } finally {
      this.inFlight--
      if (this.inFlight === 0) this.announce(this.newestFailed ? 'error' : 'saved')
    }
  }

  private announce(state: 'saving' | 'saved' | 'error') {
    window.dispatchEvent(new CustomEvent('side-menu-save', { detail: { state } }))
  }

  private async request() {
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''

    const response = await fetch(this.urlValue, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: this.payload() }),
      credentials: 'same-origin'
    })

    if (!response.ok) throw new Error(`Side menu save failed with ${response.status}`)
  }

  // Hiding is the one edit whose result cannot be seen until edit mode is left,
  // so the bar counts it.
  private syncHint() {
    if (!this.hasHintTarget) return

    this.hintTarget.textContent =
      this.hint(this.element.querySelectorAll('li[data-key][data-hidden="true"]').length)
  }

  private hint(count: number): string {
    if (count === 0) return this.hintDefaultValue
    if (count === 1) return this.hintHiddenOneValue

    return this.hintHiddenOtherValue.replace('%{count}', String(count))
  }
}
